import { suppliesShippingRateStepSchema } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  DEFAULT_SUPPLIES_SHIPPING_LABEL,
  SuppliesShippingRateMode,
} from "@redotech/redo-flows-core-schema-common/common";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { FrontendStepType, StepTypeDetailsProps } from "..";
import {
  SuppliesInspectorShell,
  SuppliesMoneyField,
  SuppliesPercentField,
  SuppliesTextField,
} from "./supplies-inspector-inputs";
import {
  SuppliesRate,
  SuppliesRateStep,
  toSuppliesRate,
} from "./supplies-shipping-step-values";

type RateStep = SuppliesRateStep;
type Rate = SuppliesRate;

const FIXED = SuppliesShippingRateMode.FIXED;

function modeLabel(mode: SuppliesShippingRateMode): string {
  switch (mode) {
    case SuppliesShippingRateMode.FIXED:
      return t("Fixed amount");
    case SuppliesShippingRateMode.PERCENT_OF_CART:
      return t("Percent of cart value");
  }
}

const MODE_OPTIONS = Object.values(SuppliesShippingRateMode);

function modeOption(mode: SuppliesShippingRateMode) {
  return {
    id: mode,
    type: "text" as const,
    text: modeLabel(mode),
    value: mode,
  };
}

// Switching mode replaces the rate: the two carry different operands.
function emptyRateOfMode(mode: SuppliesShippingRateMode): Rate {
  return mode === SuppliesShippingRateMode.PERCENT_OF_CART
    ? { mode, percentBasisPoints: 0 }
    : { mode, amountMinorUnits: 0 };
}

const SuppliesShippingRateInspector = memo(
  function SuppliesShippingRateInspector(
    props: StepTypeDetailsProps<RateStep>,
  ) {
    const { t } = useTranslation();
    const { step, setStep, onClose, readonly } = props;
    const rate = toSuppliesRate(step.rate);
    return (
      <SuppliesInspectorShell
        onClose={onClose}
        subtitle={t(
          "Terminal step: the cart is charged this amount and routing stops.",
        )}
        title={t("Set shipping cost")}
      >
        <Flex dir="column" gap="md">
          <Text fontSize="xxs" textColor="secondary">
            {t("Rate")}
          </Text>
          <RedoSingleSelectDropdownInput
            disabled={readonly}
            options={MODE_OPTIONS.map(modeOption)}
            optionSelected={({ item }) =>
              setStep({ ...step, rate: emptyRateOfMode(item.value) })
            }
            selectedItem={rate ? modeOption(rate.mode) : modeOption(FIXED)}
            size="xs"
          />
        </Flex>

        {rate?.mode === SuppliesShippingRateMode.PERCENT_OF_CART ? (
          <SuppliesPercentField
            basisPoints={rate.percentBasisPoints}
            description={t(
              "Applied to the cart total before tax, after any discount.",
            )}
            disabled={readonly}
            label={t("Percent of cart")}
            onCommit={(percentBasisPoints) =>
              setStep({
                ...step,
                rate: {
                  mode: SuppliesShippingRateMode.PERCENT_OF_CART,
                  percentBasisPoints,
                },
              })
            }
          />
        ) : (
          <SuppliesMoneyField
            description={t("Enter 0.00 for free shipping.")}
            disabled={readonly}
            label={t("Shipping cost")}
            minorUnits={
              rate?.mode === SuppliesShippingRateMode.FIXED
                ? rate.amountMinorUnits
                : undefined
            }
            onCommit={(amountMinorUnits) =>
              setStep({
                ...step,
                rate: {
                  mode: SuppliesShippingRateMode.FIXED,
                  amountMinorUnits,
                },
              })
            }
          />
        )}
        <SuppliesTextField
          description={t(
            'Shown to the merchant at checkout. Defaults to "{{defaultLabel}}".',
            { defaultLabel: DEFAULT_SUPPLIES_SHIPPING_LABEL },
          )}
          disabled={readonly}
          label={t("Label")}
          onCommit={(label) => setStep({ ...step, label })}
          placeholder={DEFAULT_SUPPLIES_SHIPPING_LABEL}
          value={step.label ?? undefined}
        />
      </SuppliesInspectorShell>
    );
  },
);

export const suppliesShippingRateStepType: FrontendStepType<RateStep> = {
  type: "custom",
  valid: (step) => suppliesShippingRateStepSchema.safeParse(step).success,
  customInspector: SuppliesShippingRateInspector,
};
